// SettingsModal — read-only platform metadata (queue mode, GitHub repo) +
// WIP limits + live WIP scopes. Provider keys are configured via env on the
// review-loop server, not here — the UI reads /config to confirm which
// keys are present without ever surfacing their values.

import { useEffect, useState } from 'react';
import * as api from '../lib/api';
import { useConfig } from '../hooks/useConfig';
import { ConfigDefaultsPanel } from './ConfigDefaultsPanel';
import { useToast } from '../hooks/useToast';
import type { CodexOauthStatusResponse, WipScopeRow } from '../lib/types';
import { COLUMNS } from '../lib/columns';

interface SettingsModalProps {
  onClose: () => void;
}

export function SettingsModal({ onClose }: SettingsModalProps) {
  const { config, error: configError } = useConfig();
  const [wip, setWip] = useState<WipScopeRow[] | null>(null);
  const [wipError, setWipError] = useState<string | null>(null);
  const [codexAuth, setCodexAuth] = useState<CodexOauthStatusResponse | 'unknown' | null>(null);
  const { toast, show: showToast } = useToast();

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.getWip();
        if (!cancelled) setWip(res.scopes);
      } catch (err) {
        if (!cancelled) setWipError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // codex-oauth preflight — best-effort. Never blocks rendering; on failure
    // the badge falls back to "unknown" rather than disappearing.
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.getCodexOauthStatus();
        if (!cancelled) setCodexAuth(res);
      } catch {
        if (!cancelled) setCodexAuth('unknown');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>configure</h3>
          <button className="icon-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <section>
            <h4>platform</h4>
            {!config && !configError && (
              <div className="kv-row"><span className="mono dim">loading…</span></div>
            )}
            {configError && (
              <div className="kv-row"><span className="mono dim">error: {configError}</span></div>
            )}
            {config && (
              <>
                <div className="kv-row">
                  <span className="mono">queue mode</span>
                  <span className="mono dim">{config.queue_mode}</span>
                </div>
                <div className="kv-row">
                  <span className="mono">github</span>
                  <span className="mono dim">
                    {config.github.owner && config.github.repo
                      ? `${config.github.owner}/${config.github.repo}`
                      : '— (not configured)'}
                  </span>
                </div>
                <div className="kv-row">
                  <span className="mono">providers</span>
                  <span className="mono dim">
                    {config.providers_configured.length > 0
                      ? config.providers_configured.join(', ')
                      : '— (none)'}
                  </span>
                </div>
                <div className="kv-row">
                  <span className="mono">codex-oauth</span>
                  <span className="mono dim">{renderCodexAuth(codexAuth)}</span>
                </div>
                <div className="kv-row">
                  <span className="mono">jira</span>
                  <span className="mono dim">{config.jira_enabled ? 'enabled' : 'disabled'}</span>
                </div>
                <div className="kv-row">
                  <span className="mono">version</span>
                  <span className="mono dim">{config.version}</span>
                </div>
              </>
            )}
          </section>
          <section>
            <h4>WIP limits (column targets)</h4>
            {COLUMNS.filter((c) => c.limit).map((c) => (
              <div className="kv-row" key={c.id}>
                <span className="mono">{c.id}</span>
                <span className="mono dim">{c.limit}</span>
              </div>
            ))}
          </section>
          <ConfigDefaultsPanel onToast={showToast} />
          <section>
            <h4>WIP scopes (live · /wip)</h4>
            {wipError && <div className="kv-row"><span className="mono dim">error: {wipError}</span></div>}
            {!wipError && wip === null && <div className="kv-row"><span className="mono dim">loading…</span></div>}
            {wip && wip.length === 0 && (
              <div className="kv-row"><span className="mono dim">no scopes registered</span></div>
            )}
            {wip && wip.map((s) => (
              <div className="kv-row" key={s.scope}>
                <span className="mono">{s.scope}</span>
                <span className="mono dim">{s.in_flight} / {s.cap}</span>
              </div>
            ))}
          </section>
        </div>
        {toast && <div className={`toast toast-${toast.tone}`}>{toast.message}</div>}
      </div>
    </div>
  );
}

// codex-oauth badge rendering. Four canonical states from the preflight
// endpoint plus "loading" (null) and "unknown" (request failed).
function renderCodexAuth(
  status: CodexOauthStatusResponse | 'unknown' | null,
): string {
  if (status === null) return 'loading…';
  if (status === 'unknown') return 'unknown (status endpoint unreachable)';
  if (status.host_supported === false) {
    return `not available on this host (${status.deploy_env ?? 'ephemeral'})`;
  }
  switch (status.state) {
    case 'ready':
      return `ready · ChatGPT (${status.auth_file_path})`;
    case 'ready-api-key':
      return `misconfig · API-key only (use the openai provider instead)`;
    case 'missing':
      return `not logged in · run \`codex login\``;
    case 'unreadable':
      return `unreadable · ${status.reason ?? 'parse failed'}`;
    case 'stale':
      return `stale · re-run \`codex login\``;
    default:
      return 'unknown';
  }
}
